import {
    createAsyncThunk,
    createEntityAdapter,
    createSlice
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import * as api from '../services/api';
import {
    retrieveUserProfile,
    storeUserProfile
} from '../services/sessionStorage';
import { RootState } from '../store';
import { UserProfile } from '../types/user';

const userAdapter = createEntityAdapter();

export interface UserState {
    loading: boolean;
    profile: UserProfile | null;
    isAllowedAdmin: boolean | null;
}

const retrievedProfile = retrieveUserProfile();
const initialState = userAdapter.getInitialState({
    loading: false,
    profile: retrievedProfile,
    // From login/mfa response only; no separate admin/me call (non-admins never see that endpoint)
    isAllowedAdmin: retrievedProfile ? (retrievedProfile.isAllowedAdmin === true) : null,
} as UserState);

export const login = createAsyncThunk(
    'user/login',
    async (
        _options: { email: string; password: string },
        { rejectWithValue }
    ) => {
        try {
            return await api.login(_options.email, _options.password);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const signUp = createAsyncThunk(
    'user/signUp',
    async (
        _options: { 
            first_name: string; 
            last_name: string; 
            email: string; 
            password: string; 
            tier_id?: number;
            terms_consent?: {
                terms_version: string;
                consent_timestamp: number;
                browser_user_agent?: string;
                browser_language?: string;
                browser_platform?: string;
                consent_data?: Record<string, any>;
            };
        },
        { rejectWithValue }
    ) => {
        try {
            return await api.signUp(
                _options.first_name,
                _options.last_name,
                _options.email,
                _options.password,
                _options.tier_id,
                _options.terms_consent
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const getTiers = createAsyncThunk(
    'user/getTiers',
    async (_options: {}, { rejectWithValue }) => {
        try {
            return await api.getTiers();
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const mfaSetup = createAsyncThunk(
    'user/mfaSetup',
    async (_options: {}, { rejectWithValue }) => {
        try {
            return await api.mfaSetup();
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const mfaVerifySetup = createAsyncThunk(
    'user/mfaVerifySetup',
    async (_options: { code: string }, { rejectWithValue }) => {
        try {
            return await api.mfaVerifySetup(_options.code);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const mfaVerifyLogin = createAsyncThunk(
    'user/mfaVerifyLogin',
    async (_options: { email: string; code: string }, { rejectWithValue }) => {
        try {
            return await api.mfaVerifyLogin(_options.email, _options.code);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const changePassword = createAsyncThunk(
    'user/changePassword',
    async (
        _options: { email: string; old_password: string; new_password: string },
        { rejectWithValue }
    ) => {
        try {
            return await api.changePassword(
                _options.email,
                _options.old_password,
                _options.new_password
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const fetchAdminMe = createAsyncThunk(
    'user/fetchAdminMe',
    async (_options: {}, { rejectWithValue }) => {
        try {
            return await api.getAdminMe();
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const adminForceReprocessMedicalDevice = createAsyncThunk(
    'user/adminForceReprocessMedicalDevice',
    async (
        _options: { kNumbers: string[] },
        { rejectWithValue }
    ) => {
        try {
            return await api.adminForceReprocessMedicalDevice(_options.kNumbers);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const verifyToken = createAsyncThunk(
    'user/verifyToken',
    async (_options: { token: string }, { rejectWithValue }) => {
        try {
            return await api.verifyToken(_options.token);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async (
        _options: { token: string; password: string },
        { rejectWithValue }
    ) => {
        try {
            return await api.resetPassword(_options.token, _options.password);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const forgetPassword = createAsyncThunk(
    'user/forgetPassword',
    async (_options: { email: string }, { rejectWithValue }) => {
        try {
            return await api.forgetPassword(_options.email);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// This API call returns the list the all PDF documents
export const getDocumentList = createAsyncThunk(
    'convert/getDocumentList',
    async (_options: { numDocs?: number }, { rejectWithValue }) => {
        try {
            return await api.getDocuments(_options.numDocs);
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// This API call returns the list of FDA medical device K-number documents
export const getMedicalDeviceKDocuments = createAsyncThunk(
    'fda/getMedicalDeviceKDocuments',
    async (
        _options: { numDocs?: number; forceReprocess?: boolean; category?: string; subCategory?: string; offset?: number; company?: string },
        { rejectWithValue }
    ) => {
        try {
            return await api.getMedicalDeviceKDocuments(
                _options.numDocs,
                _options.forceReprocess,
                _options.category,
                _options.subCategory,
                _options.offset,
                _options.company
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);


const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(login.pending, (state) => {
            state.loading = true;
            state.profile = null;
        });
        builder.addCase(login.fulfilled, (state, { payload }) => {
            state.loading = false;
            const user = { ...payload.data };
            delete user.auth_token;
            state.profile = user;
            state.isAllowedAdmin = user.isAllowedAdmin === true;
            storeUserProfile(user);
        });
        builder.addCase(login.rejected, (state) => {
            state.loading = false;
        });
        builder.addCase(getMedicalDeviceKDocuments.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getMedicalDeviceKDocuments.fulfilled, (state, { payload }) => {
            state.loading = false;
            // Store the medical device documents in state if needed
        });
        builder.addCase(getMedicalDeviceKDocuments.rejected, (state) => {
            state.loading = false;
        });
        // MFA verify login - same as login, updates user profile
        builder.addCase(mfaVerifyLogin.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(mfaVerifyLogin.fulfilled, (state, { payload }) => {
            state.loading = false;
            const user = { ...payload.data };
            delete user.auth_token;
            state.profile = user;
            state.isAllowedAdmin = user.isAllowedAdmin === true;
            storeUserProfile(user);
        });
        builder.addCase(mfaVerifyLogin.rejected, (state) => {
            state.loading = false;
        });
        builder.addCase(fetchAdminMe.fulfilled, (state, { payload }) => {
            state.isAllowedAdmin = payload?.data?.isAllowedAdmin === true;
        });
        builder.addCase(fetchAdminMe.rejected, (state) => {
            state.isAllowedAdmin = false;
        });
        // Signup - also updates user profile
        builder.addCase(signUp.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(signUp.fulfilled, (state, { payload }) => {
            state.loading = false;
            if (payload?.data) {
                const user = { ...payload.data };
                delete user.auth_token;
                state.profile = user;
                storeUserProfile(user);
            }
        });
        builder.addCase(signUp.rejected, (state) => {
            state.loading = false;
        });
    }
});

export default userSlice.reducer;

interface UserSelectorsType {
    loading: boolean;
    profile: UserProfile | null;
    isAllowedAdmin: boolean | null;
}

export const UserSelectors = (): UserSelectorsType => {
    const loading = useSelector((state: RootState) => state.user.loading);
    const profile = useSelector((state: RootState) => state.user.profile);
    const isAllowedAdmin = useSelector((state: RootState) => state.user.isAllowedAdmin);

    return {
        profile,
        loading,
        isAllowedAdmin
    };
};
